'use client';
import type { InterfaceEntity } from '@unifyapps/defs/types/interface';
import { useEffect, useMemo } from 'react';
import { NAV_DIRECTION_ATTRIBUTE } from '../../no-code/const';

const BASE_TRANSITION_STYLES = `
  [data-name="workspace"] {
    view-transition-name: main-content;
  }
  
  ::view-transition-new(root),
  ::view-transition-old(root) {
    animation: none;
  }
  
  ::view-transition-group(main-content) {
    overflow: hidden;
  }
  
  ::view-transition-old(main-content),
  ::view-transition-new(main-content) {
    animation-duration: 0.35s;
    animation-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  }
`;

const KEYFRAMES = `
  @keyframes fade-out { to { opacity: 0; } }
  @keyframes fade-in { from { opacity: 0; } }
  @keyframes move-ltr-out { to { transform: translateX(-100%); } }
  @keyframes move-ltr-in { from { transform: translateX(100%); } }
  @keyframes move-rtl-out { to { transform: translateX(100%); } }
  @keyframes move-rtl-in { from { transform: translateX(-100%); } }
`;

const FADE_STYLES = `
  ::view-transition-old(main-content) { animation-name: fade-out; }
  ::view-transition-new(main-content) { animation-name: fade-in; }
`;

const SLIDE_HORIZONTAL_STYLES = `
  ::view-transition-old(main-content) { animation-name: move-ltr-out; }
  ::view-transition-new(main-content) { animation-name: move-ltr-in; }

  html[${NAV_DIRECTION_ATTRIBUTE}="back"]::view-transition-old(main-content) { animation-name: move-rtl-out; }
  html[${NAV_DIRECTION_ATTRIBUTE}="back"]::view-transition-new(main-content) { animation-name: move-rtl-in; }

  html[dir="rtl"]::view-transition-old(main-content) { animation-name: move-rtl-out; }
  html[dir="rtl"]::view-transition-new(main-content) { animation-name: move-rtl-in; }

  html[dir="rtl"][${NAV_DIRECTION_ATTRIBUTE}="back"]::view-transition-old(main-content) { animation-name: move-ltr-out; }
  html[dir="rtl"][${NAV_DIRECTION_ATTRIBUTE}="back"]::view-transition-new(main-content) { animation-name: move-ltr-in; }
`;

export function handleBackNavigation() {
  // add attribute to html to indicate back navigation, used in css for different animation
  document.documentElement.setAttribute(NAV_DIRECTION_ATTRIBUTE, 'back');
  // remove the attribute after some time to reset
  setTimeout(() => {
    document.documentElement.removeAttribute(NAV_DIRECTION_ATTRIBUTE);
  }, 1000);
}

export function PageTransitions({
  type,
}: {
  type: InterfaceEntity['properties']['navigation']['pageTransition'];
}) {
  useEffect(() => {
    if (type && type === 'SLIDE_HORIZONTAL') {
      window.addEventListener('popstate', handleBackNavigation);

      return () => {
        window.removeEventListener('popstate', handleBackNavigation);
      };
    }
  }, [type]);

  const htmlAttributes = useMemo(() => {
    const styles = [
      BASE_TRANSITION_STYLES,
      KEYFRAMES,
      type === 'FADE' ? FADE_STYLES : '',
      type === 'SLIDE_HORIZONTAL' ? SLIDE_HORIZONTAL_STYLES : '',
    ]
      .filter(Boolean)
      .join('\n');

    return {
      __html: styles,
    };
  }, [type]);

  if (!type) {
    return null;
  }

  return (
    <style
      // biome-ignore lint/security/noDangerouslySetInnerHtml: style is trusted
      dangerouslySetInnerHTML={htmlAttributes}
    />
  );
}
